import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Box, Typography, Button, Alert, Paper, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { PlayArrow as PlayArrowIcon, ArrowBack as ArrowBackIcon, Videocam as VideocamIcon, Stop as StopIcon } from '@mui/icons-material'
import { recordingService, type RecordingSession, type RecordingTask } from '@/services/recordingService'
import { API_CONFIG } from '@/config/api'
import { useAuth } from '@/contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { apiUrlBuilder } from '@/utils/apiUrlBuilder'
import { resolvePlayableVideo } from '@/utils/playableVideo'

type RecordingFlow = 'consent' | 'countdown' | 'recording' | 'completed'

function pickSupportedMediaRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return undefined
  }
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4',
  ]
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) {
      return mime
    }
  }
  return undefined
}

export default function RecordingSessionView(): React.ReactElement {
  const { t } = useTranslation('recording')
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const reactionVideoRef = useRef<HTMLVideoElement | null>(null)
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const [isStarting, setIsStarting] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [recordingSession, setRecordingSession] = useState<RecordingSession | null>(null)
  const [recordingTask, setRecordingTask] = useState<RecordingTask | null>(null)
  const [isRecording, setIsRecording] = useState<boolean>(false)
  const [recordingFlow, setRecordingFlow] = useState<RecordingFlow>('consent')
  const [countdown, setCountdown] = useState<number | null>(null)
  const [consents, setConsents] = useState({
    rodo: false,
    recording: false,
    research: false,
  })
  const [showCameraPreview, setShowCameraPreview] = useState<boolean>(false)
  const [showCompletionDialog, setShowCompletionDialog] = useState<boolean>(false)
  const [reactionVideoBlobUrl, setReactionVideoBlobUrl] = useState<string | null>(null)
  const [reactionVideoReady, setReactionVideoReady] = useState<boolean>(false)
  
  const sessionId = searchParams.get('sessionId')
  const taskId = searchParams.get('taskId')
  const reactionVideoUrlParam = searchParams.get('reactionVideoUrl')
  const note = searchParams.get('note')

  const effectiveReactionVideoUrl =
    recordingTask?.reaction_video_url ??
    recordingSession?.reaction_video_url ??
    reactionVideoUrlParam ??
    null
  const hasReactionVideo = Boolean(effectiveReactionVideoUrl)

  const recordedBlobsRef = useRef<Blob[]>([])
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const reactionVideoBlobUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (sessionId) {
      recordingService.getRecordingSession(parseInt(sessionId))
        .then(setRecordingSession)
        .catch((err) => {
          console.error('Error loading session:', err)
          setError(t('errors.loadSession'))
        })
    }
  }, [sessionId])

  useEffect(() => {
    if (taskId) {
      recordingService.getRecordingTask(parseInt(taskId))
        .then(setRecordingTask)
        .catch((err) => {
          console.error('Error loading task:', err)
        })
    }
  }, [taskId])

  useEffect(() => {
    setReactionVideoReady(false)
    if (!effectiveReactionVideoUrl) {
      setReactionVideoBlobUrl(null)
      return
    }

    let cancelled = false

    const loadReactionVideo = async () => {
      try {
        // Resolve to a presigned S3 URL (or a blob: URL for local storage) and
        // assign it directly to the <video>, so the recording never streams
        // through the single app worker.
        const resolveOnce = async (): Promise<string> => {
          const token = localStorage.getItem('access_token')
          if (!token) {
            throw new Error('No authentication token available')
          }
          return await resolvePlayableVideo(effectiveReactionVideoUrl, token)
        }

        const refreshToken = async (): Promise<void> => {
          const refreshTokenValue = localStorage.getItem('refresh_token')
          if (!refreshTokenValue) {
            throw new Error('No refresh token available')
          }

          const response = await fetch(apiUrlBuilder.build(API_CONFIG.ENDPOINTS.TOKEN.REFRESH), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh: refreshTokenValue }),
          })

          if (!response.ok) {
            throw new Error('Failed to refresh token')
          }

          const data = await response.json()
          localStorage.setItem('access_token', data.access)
          localStorage.setItem('refresh_token', data.refresh)
        }

        let resolvedUrl: string
        try {
          resolvedUrl = await resolveOnce()
        } catch (err) {
          if (err instanceof Error && err.message.includes('401')) {
            await refreshToken()
            resolvedUrl = await resolveOnce()
          } else {
            throw err
          }
        }

        if (cancelled) {
          return
        }

        if (reactionVideoBlobUrlRef.current) {
          URL.revokeObjectURL(reactionVideoBlobUrlRef.current)
        }
        reactionVideoBlobUrlRef.current = resolvedUrl
        setReactionVideoBlobUrl(resolvedUrl)
      } catch (err) {
        if (cancelled) {
          return
        }
        console.error('Error loading reaction video:', err)
        setError(t('errors.loadReactionVideo'))
        setReactionVideoBlobUrl(null)
      }
    }

    loadReactionVideo()

    return () => {
      cancelled = true
      if (reactionVideoBlobUrlRef.current) {
        URL.revokeObjectURL(reactionVideoBlobUrlRef.current)
        reactionVideoBlobUrlRef.current = null
      }
    }
  }, [effectiveReactionVideoUrl])

  useEffect(() => {
    if (reactionVideoBlobUrl && reactionVideoRef.current) {
      console.log('Setting reaction video blob URL')
      reactionVideoRef.current.src = reactionVideoBlobUrl
      reactionVideoRef.current.load()
      
      const handleLoadedData = () => {
        console.log('Reaction video loaded successfully in video element')
      }
      
      const handleError = (e: Event) => {
        console.error('Error loading reaction video in video element:', e)
        setError(t('errors.loadReactionVideo'))
      }
      
      reactionVideoRef.current.addEventListener('loadeddata', handleLoadedData)
      reactionVideoRef.current.addEventListener('error', handleError)
      
      return () => {
        if (reactionVideoRef.current) {
          reactionVideoRef.current.removeEventListener('loadeddata', handleLoadedData)
          reactionVideoRef.current.removeEventListener('error', handleError)
        }
      }
    }
  }, [reactionVideoBlobUrl])

  const stopTracks = (stream: MediaStream | null): void => {
    if (!stream) return
    stream.getTracks().forEach((track) => track.stop())
  }

  const startCamera = useCallback(async (showPreview: boolean = true): Promise<void> => {
    if (isStarting || mediaStream) return
    setIsStarting(true)
    setError('')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30, max: 30 },
        },
        audio: true,
      })
      setMediaStream(stream)
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        if (showPreview) {
          await videoRef.current.play()
        }
      }
    } catch (err) {
      console.error('Camera access error:', err)
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError(t('errors.cameraDenied'))
        } else if (err.name === 'NotFoundError') {
          setError(t('errors.cameraNotFound'))
        } else if (err.name === 'NotReadableError') {
          setError(t('errors.cameraInUse'))
        } else {
          setError(`Błąd kamery: ${err.message}`)
        }
      } else {
        setError(t('errors.cameraAccess'))
      }
    } finally {
      setIsStarting(false)
    }
  }, [isStarting, mediaStream])


  const startRecording = useCallback(async (): Promise<void> => {
    if ((!sessionId && !taskId) || !mediaStream) {
      console.error('Cannot start recording: missing sessionId/taskId or mediaStream', { sessionId, taskId, mediaStream: !!mediaStream })
      return
    }

    try {
      console.log('Starting recording...', { hasReactionVideo })

      if (reactionVideoRef.current && hasReactionVideo) {
        reactionVideoRef.current.currentTime = 0
        await reactionVideoRef.current.play()
      }

      if (sessionId && recordingSession && recordingSession.status === 'created') {
        await recordingService.startRecordingSession(parseInt(sessionId))
        const updatedSession = await recordingService.getRecordingSession(parseInt(sessionId))
        setRecordingSession(updatedSession)
      } else if (taskId) {
        await recordingService.updateRecordingTaskStatus(parseInt(taskId), 'in_progress')
      }

      recordedBlobsRef.current = []
      const preferredMime = pickSupportedMediaRecorderMimeType()
      const mediaRecorder = preferredMime
        ? new MediaRecorder(mediaStream, { mimeType: preferredMime })
        : new MediaRecorder(mediaStream)

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedBlobsRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        const blobType = mediaRecorder.mimeType || 'video/webm'
        const blob = new Blob(recordedBlobsRef.current, { type: blobType })
        
        if (blob.size === 0) {
          setError(t('errors.emptyRecording'))
          setIsRecording(false)
          setRecordingFlow('consent')
          return
        }
        
        try {
          if (sessionId) {
            await recordingService.stopRecordingSession(parseInt(sessionId), {
              videoFile: blob
            })
            
            const finalSession = await recordingService.getRecordingSession(parseInt(sessionId))
            setRecordingSession(finalSession)
          } else if (taskId) {
            await recordingService.completeRecordingTask(parseInt(taskId), blob)
          }
          
          setIsRecording(false)
          setError('')
          setRecordingFlow('completed')
          setShowCompletionDialog(true)
        } catch (err) {
          console.error('Error saving recording:', err)
          setError(t('errors.saveRecording'))
          setRecordingFlow('consent')
        }
      }

      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start(1000)
      setIsRecording(true)
      console.log('Recording started successfully, isRecording:', true)
    } catch (err) {
      console.error('Failed to start recording:', err)
      setError(t('errors.startRecording'))
      setRecordingFlow('consent')
    }
  }, [sessionId, taskId, mediaStream, recordingSession, hasReactionVideo])

  const stopRecording = useCallback(async (): Promise<void> => {
    if (!mediaRecorderRef.current) return

    // Stop reaction video if playing
    if (reactionVideoRef.current && !reactionVideoRef.current.paused) {
      reactionVideoRef.current.pause()
    }

    try {
      mediaRecorderRef.current.stop()
      mediaRecorderRef.current = null
    } catch (err) {
      console.error('Failed to stop recording:', err)
      setError(t('errors.stopRecording'))
    }
  }, [])

  useEffect(() => {
    // Auto-stop recording when reaction video ends
    if (reactionVideoRef.current && isRecording && recordingFlow === 'recording') {
      const handleVideoEnd = () => {
        if (mediaRecorderRef.current) {
          stopRecording()
        }
      }
      
      reactionVideoRef.current.addEventListener('ended', handleVideoEnd)
      
      return () => {
        if (reactionVideoRef.current) {
          reactionVideoRef.current.removeEventListener('ended', handleVideoEnd)
        }
      }
    }
  }, [isRecording, stopRecording, recordingFlow])

  // Handle countdown
  useEffect(() => {
    if (recordingFlow === 'countdown' && countdown === null) {
      setCountdown(3)
    }
  }, [recordingFlow, countdown])

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      countdownIntervalRef.current = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
    } else if (countdown === 0) {
      setCountdown(null)
      setRecordingFlow('recording')
      startRecording()
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearTimeout(countdownIntervalRef.current)
      }
    }
  }, [countdown, startRecording])

  useEffect(() => {
    if (recordingFlow === 'countdown' && !mediaStream) {
      startCamera(false)
    }
  }, [recordingFlow, mediaStream, startCamera])

  // Ensure camera preview is displayed when showCameraPreview is true and mediaStream exists
  useEffect(() => {
    if (showCameraPreview && mediaStream && videoRef.current && recordingFlow === 'consent') {
      videoRef.current.srcObject = mediaStream
      videoRef.current.play().catch(console.error)
    }
  }, [showCameraPreview, mediaStream, recordingFlow])

  // Ensure reaction video plays when recording starts
  useEffect(() => {
    if (recordingFlow === 'recording' && reactionVideoRef.current && hasReactionVideo && reactionVideoBlobUrl) {
      const playVideo = async () => {
        try {
          console.log('Attempting to play reaction video, readyState:', reactionVideoRef.current?.readyState)
          if (reactionVideoRef.current) {
            // Ensure video has the correct source
            if (reactionVideoRef.current.src !== reactionVideoBlobUrl) {
              console.log('Setting video source to blob URL')
              reactionVideoRef.current.src = reactionVideoBlobUrl
              reactionVideoRef.current.load()
            }
            
            reactionVideoRef.current.currentTime = 0
            
            // Wait for video to be ready with longer timeout
            if (reactionVideoRef.current.readyState < 2) {
              console.log('Waiting for video to be ready...')
              await new Promise((resolve) => {
                const timeout = setTimeout(() => {
                  console.warn('Video load timeout, attempting to play anyway')
                  resolve(undefined)
                }, 10000)
                
                const handleCanPlay = () => {
                  console.log('Video can play now')
                  clearTimeout(timeout)
                  if (reactionVideoRef.current) {
                    reactionVideoRef.current.removeEventListener('canplay', handleCanPlay)
                    reactionVideoRef.current.removeEventListener('canplaythrough', handleCanPlayThrough)
                  }
                  resolve(undefined)
                }
                
                const handleCanPlayThrough = () => {
                  console.log('Video can play through now')
                  clearTimeout(timeout)
                  if (reactionVideoRef.current) {
                    reactionVideoRef.current.removeEventListener('canplay', handleCanPlay)
                    reactionVideoRef.current.removeEventListener('canplaythrough', handleCanPlayThrough)
                  }
                  resolve(undefined)
                }
                
                if (reactionVideoRef.current) {
                  reactionVideoRef.current.addEventListener('canplay', handleCanPlay)
                  reactionVideoRef.current.addEventListener('canplaythrough', handleCanPlayThrough)
                  
                  // If already ready, resolve immediately
                  if (reactionVideoRef.current.readyState >= 2) {
                    clearTimeout(timeout)
                    reactionVideoRef.current.removeEventListener('canplay', handleCanPlay)
                    reactionVideoRef.current.removeEventListener('canplaythrough', handleCanPlayThrough)
                    resolve(undefined)
                  }
                }
              })
            }
            
            console.log('Playing reaction video, readyState:', reactionVideoRef.current.readyState)
            await reactionVideoRef.current.play()
            console.log('Reaction video playing successfully')
          }
        } catch (err) {
          console.error('Error playing reaction video:', err)
          setError(t('errors.playReactionVideo'))
        }
      }
      playVideo()
    }
  }, [recordingFlow, hasReactionVideo, reactionVideoBlobUrl])

  // Ensure camera is displayed when recording without reaction video
  useEffect(() => {
    if (recordingFlow === 'recording' && !hasReactionVideo && videoRef.current && mediaStream) {
      videoRef.current.srcObject = mediaStream
      videoRef.current.play().catch(console.error)
    }
  }, [recordingFlow, hasReactionVideo, mediaStream])

  useEffect(() => {
    return () => {
      stopTracks(mediaStream)
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop()
      }
      if (countdownIntervalRef.current) {
        clearTimeout(countdownIntervalRef.current)
      }
    }
  }, [mediaStream])

  const handleConsentChange = (consentType: keyof typeof consents) => {
    setConsents(prev => ({
      ...prev,
      [consentType]: !prev[consentType]
    }))
  }

  const allConsentsAccepted = consents.rodo && consents.recording && consents.research

  const handleStartFromConsent = () => {
    if (allConsentsAccepted) {
      setRecordingFlow('countdown')
    }
  }

  const handleTestCamera = async () => {
    if (!mediaStream) {
      await startCamera(true)
      setShowCameraPreview(true)
    } else {
      const newShowPreview = !showCameraPreview
      setShowCameraPreview(newShowPreview)
      if (newShowPreview && videoRef.current) {
        videoRef.current.srcObject = mediaStream
        videoRef.current.play().catch(console.error)
      }
    }
  }

  const handleCloseCompletionDialog = () => {
    setShowCompletionDialog(false)
    navigate(getBackUrl())
  }

  const getBackUrl = () => {
    if (recordingSession?.project_participant) {
      const projectId = recordingSession.project_participant.project
      const isResearcher = user && recordingSession.created_by === user.id
      if (isResearcher) {
        return `/projects?projectId=${projectId}`
      }
      return `/participant/projects/${projectId}`
    }
    if (recordingTask?.project_participant) {
      const projectId = recordingTask.project_participant.project
      const isResearcher = user && recordingTask.created_by === user.id
      if (isResearcher) {
        return `/projects?projectId=${projectId}`
      }
      return `/participant/projects/${projectId}`
    }
    if (user) {
      const isResearcher = recordingSession?.created_by === user.id || recordingTask?.created_by === user.id
      if (isResearcher) {
        return '/projects'
      }
    }
    return '/participant/dashboard'
  }

  if (!sessionId && !taskId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('missingSessionOrTask')}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projects')}
          sx={{ mt: 2 }}
        >
          {t('backToProjects')}
        </Button>
      </Box>
    )
  }

  // Consent screen
  if (recordingFlow === 'consent') {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '800px', mx: 'auto' }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(getBackUrl())}
            size="small"
          >
            {t('back')}
          </Button>
          <Typography variant="h4">{t('sessionTitle')}</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {(recordingSession || taskId) && note && (
          <Paper sx={{ p: 2, mb: 2, backgroundColor: '#eff6ff', border: '2px solid #3b82f6' }}>
            <Typography variant="h6" gutterBottom>
              {recordingSession
                ? t('statusLabel', { status: recordingSession.status })
                : t('recordingTask')}
            </Typography>
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('instruction')}
              </Typography>
              <Typography variant="body1">
                {decodeURIComponent(note)}
              </Typography>
            </Box>
          </Paper>
        )}

        <Paper sx={{ p: { xs: 2.5, md: 4 } }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            {t('consentTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('consentIntro')}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={consents.rodo}
                  onChange={() => handleConsentChange('rodo')}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {t('consentRodoTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('consentRodoBody')}
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, alignItems: 'flex-start' }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={consents.recording}
                  onChange={() => handleConsentChange('recording')}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {t('consentRecordingTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('consentRecordingBody')}
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, alignItems: 'flex-start' }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={consents.research}
                  onChange={() => handleConsentChange('research')}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {t('consentResearchTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('consentResearchBody')}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<VideocamIcon />}
              onClick={handleTestCamera}
              disabled={isStarting}
              sx={{ mb: 2 }}
            >
              {showCameraPreview ? t('hideCameraPreview') : t('testCamera')}
            </Button>

            {showCameraPreview && (
              <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  style={{ 
                    width: '100%', 
                    background: '#000', 
                    borderRadius: 1,
                    maxHeight: '400px'
                  }} 
                />
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleStartFromConsent}
            disabled={!allConsentsAccepted}
            startIcon={<PlayArrowIcon />}
          >
            {t('start')}
          </Button>
        </Paper>
      </Box>
    )
  }

  // Countdown screen
  if (recordingFlow === 'countdown') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100dvh',
          backgroundColor: '#000',
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '120px', md: '200px' },
            fontWeight: 'bold',
            color: '#fff',
            animation: 'fadeInOut 1s ease-in-out',
            '@keyframes fadeInOut': {
              '0%': { opacity: 0, transform: 'scale(0.5)' },
              '50%': { opacity: 1, transform: 'scale(1.2)' },
              '100%': { opacity: 0, transform: 'scale(0.5)' },
            },
          }}
        >
          {countdown}
        </Typography>
      </Box>
    )
  }

  // Recording screen
  if (recordingFlow === 'recording') {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        overflow: 'hidden',
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9999,
      }}>
        <Box sx={{ 
          flex: 1,
          minHeight: 0,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          position: 'relative',
          width: '100%',
          paddingBottom: '100px',
        }}>
          {hasReactionVideo && reactionVideoBlobUrl ? (
            <video 
              ref={reactionVideoRef} 
              autoPlay
              playsInline
              muted={false}
              controls={false}
              src={reactionVideoBlobUrl}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                backgroundColor: '#000',
              }}
              onError={(e) => {
                console.error('Video error:', e)
                setError(t('errors.reactionVideoPlayback'))
              }}
              onLoadedData={() => {
                console.log('Reaction video loaded in view')
              }}
              onCanPlay={() => {
                console.log('Reaction video can play in view')
                setReactionVideoReady(true)
              }}
              onPlay={() => {
                console.log('Reaction video started playing in view')
              }}
            />
          ) : (
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }} 
            />
          )}
          <video
            ref={hasReactionVideo ? videoRef : undefined}
            autoPlay
            playsInline
            style={{
              display: 'none',
            }}
          />
          {hasReactionVideo && !reactionVideoReady && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                width: 44,
                height: 44,
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}
            >
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            </Box>
          )}
          {isRecording && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                backgroundColor: '#ef4444',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 'bold',
                fontSize: '18px',
                zIndex: 1000,
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  animation: 'pulse 1s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              />
              {t('recordingIndicator')}
            </Box>
          )}
        </Box>
        <Box sx={{ 
          position: 'absolute',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1001,
          flexShrink: 0,
        }}>
          <Button
            variant="contained"
            color="error"
            size="large"
            startIcon={<StopIcon />}
            onClick={stopRecording}
            disabled={!isRecording || !mediaRecorderRef.current}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '18px',
              fontWeight: 'bold',
              pointerEvents: 'auto',
            }}
          >
            {t('stopRecordingButton')}
          </Button>
        </Box>
      </Box>
    )
  }

  // Default view (should not be reached, but kept for safety)
  return (
    <>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(getBackUrl())}
            size="small"
          >
            {t('back')}
          </Button>
          <Typography variant="h4">{t('sessionTitle')}</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Completion Dialog */}
      <Dialog
        open={showCompletionDialog}
        onClose={handleCloseCompletionDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle>
          <Typography variant="h6">{t('recordingSuccess')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('recordingSavedMessage')}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('thankYouParticipation')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompletionDialog} variant="contained">
            {t('backToProject')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

